/**
 * OUT OF THE APP'S BROWSER, WITH THE BASKET (2026-10-01).
 *
 * Ten of ten DK checkout visitors in the week to 1 October came through Instagram's or Facebook's
 * in-app browser, where Apple Pay and Google Pay are never offered (applePayGate.browserIsInApp)
 * and nobody reached the payment step. A real browser converts in-app sessions two to three times
 * better, and the accepted remedy is one quiet "open in your browser" affordance. iOS has a scheme
 * for exactly that (`x-safari-https://`); Android has Chrome's intent URL with a plain https
 * fallback. Where the scheme does nothing — an app that swallows it — the page says where the
 * app's own "open in browser" lives.
 *
 * THE CART MUST SURVIVE THE HANDOFF. The in-app browser's localStorage is not the real browser's,
 * so a shopper who arrives in Safari with an empty basket has been sent backwards. The basket
 * rides in the URL the handoff opens — `items=<productId>:<variantId>:<qty>,…&handoff=1` — plus
 * the ad click the visit is remembered under (utils/adVisit.js), so the sale still credits its ad.
 * The checkout reads `items`, fetches those products through the existing window API, seeds the
 * cart once, and strips the parameter. Nothing personal travels: ids the shop publishes and counts.
 */
export const HANDOFF_ITEMS_PARAM = 'items';
export const HANDOFF_FLAG_PARAM = 'handoff';
/** The most lines a handoff carries in the ADDRESS — a basket, not a catalogue. */
export const HANDOFF_MAX_ITEMS = 20;
/** The most lines a handoff carries under a TOKEN — the server's own ceiling (checkout-handoff DTO). */
export const HANDOFF_MAX_TOKEN_LINES = 50;
/** How long the iOS scheme is given before the page assumes the app ignored it. */
export const OPEN_IN_BROWSER_WAIT_MS = 1500;
/**
 * HOW LONG ONE HAND-OFF COUNTS ONCE (2026-10-06). The basket a hand-off carries is ADDED to whatever the real
 * browser already holds — the owner's rule: "if there's another session there in the browser, add them
 * together". An address opened twice must not add twice: the same hand-off (shop + lines) is remembered on the
 * device for this long and seeded once. A token consumed on the server would do this exactly; until the
 * hand-off has one, fifteen minutes on the device is the guard.
 */
export const HANDOFF_SEEN_MS = 15 * 60 * 1000;
/** The device's memory of a hand-off already seeded: one key per shop and set of lines. */
export function handoffSeenKey(salonSlug, items) {
  return `stylon:handoff:seen:${salonSlug}:${encodeHandoffItems(items)}`;
}
/**
 * THE TOKEN (2026-10-06). The in-app page hands the server what the shopper typed and chose — contact, address,
 * delivery choice, notes, visitor key, ad click, basket lines — and gets back an opaque token (32 random bytes,
 * base64url: 43 characters). The address the real browser opens carries `handoff=<token>` and nothing typed; the
 * browser presents it ONCE (the server answers 410 to a second read and to one after fifteen minutes) and receives
 * the payload. The lines in the address (`items=`) stay as the fallback for when the server could not be reached:
 * the owner must never be stuck inside the app with no way out.
 */
export const HANDOFF_TOKEN_SHAPE = /^[A-Za-z0-9_-]{43}$/;
/** How long the in-app page waits for the server to keep the hand-off before it leaves with the basket in the address. */
export const HANDOFF_POST_WAIT_MS = 2500;
/** The device's memory of a token already presented: a second open of the same address never asks the server again. */
export function handoffTokenSeenKey(salonSlug, token) {
  return `stylon:handoff:seen:${salonSlug}:t:${token}`;
}
/** The token the address carries, or null when `handoff` is the plain flag (`1`) or absent. */
export function handoffTokenOf(search) {
  try {
    const raw = new URLSearchParams(search || '').get(HANDOFF_FLAG_PARAM) || '';
    return HANDOFF_TOKEN_SHAPE.test(raw) ? raw : null;
  } catch (_) {
    return null;
  }
}
/** How long a device is left alone after the page jumped it to the real browser once. */
export const AUTO_LEAVE_REST_MS = 24 * 60 * 60 * 1000;

/**
 * ONCE A DAY, NOT ONCE A WEBVIEW (2026-10-13). The product page's automatic jump was guarded by sessionStorage, and
 * production showed it firing on 42 of 46 in-app sessions in a day, one visitor 13 times in 45 minutes: the app's
 * browser hands a fresh webview to each link from the feed, and sessionStorage dies with the old one. localStorage
 * survives, so the guard is a timestamp there, per shop: a device that was jumped is not jumped again for a day.
 * Returns true when the jump may fire now and records it; false when it fired within the rest.
 */
export function claimAutoLeave(storage, key, now = Date.now()) {
  if (!storage) return false;
  try {
    const raw = storage.getItem(key);
    const last = raw ? Number(raw) : NaN;
    if (Number.isFinite(last) && now - last < AUTO_LEAVE_REST_MS) return false;
    storage.setItem(key, String(now));
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Leave for the real browser. One place, so a test can watch the leaving without a navigation jsdom cannot make;
 * never throws — a browser that refuses the scheme leaves the page showing, and the hint says where the app's door is.
 */
export function leaveForBrowser(href) {
  try {
    if (typeof window !== 'undefined' && window.location && href) window.location.assign(href);
  } catch (_) { /* the page stays; the hint takes over */ }
}

/** 'ios' | 'android' | 'other' — the class of phone, from its user agent. */
export function osOf(userAgent) {
  const ua = String(userAgent || '');
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

/** The browser the handoff opens, named for the button. */
export function browserNameFor(os) {
  return os === 'ios' ? 'Safari' : os === 'android' ? 'Chrome' : 'Safari/Chrome';
}

/** `items=` as the checkout reads it: product, variant and quantity per line, bounded and whole. */
export function encodeHandoffItems(items) {
  return (Array.isArray(items) ? items : [])
    .filter((i) => i && i.productId && i.variantId)
    .slice(0, HANDOFF_MAX_ITEMS)
    .map((i) => `${encodeURIComponent(String(i.productId))}:${encodeURIComponent(String(i.variantId))}:${Math.max(1, Math.trunc(Number(i.quantity) || 1))}`)
    .join(',');
}

/** The lines an `items=` names — `[{ productId, variantId, quantity }]`, or [] for anything else. */
export function handoffItemsOf(search) {
  let raw = '';
  try { raw = new URLSearchParams(search || '').get(HANDOFF_ITEMS_PARAM) || ''; } catch (_) { return []; }
  if (!raw) return [];
  const out = [];
  for (const part of raw.split(',').slice(0, HANDOFF_MAX_ITEMS)) {
    const [p, v, q] = part.split(':');
    if (!p || !v) continue;
    const quantity = Math.max(1, Math.min(999, Math.trunc(Number(q) || 1)));
    try { out.push({ productId: decodeURIComponent(p), variantId: decodeURIComponent(v), quantity }); } catch (_) { /* a mangled id is not a line */ }
  }
  return out;
}

/** The same search without the handoff's own parameters — what the address bar should keep. */
export function withoutHandoff(search) {
  try {
    const q = new URLSearchParams(search || '');
    q.delete(HANDOFF_ITEMS_PARAM);
    q.delete(HANDOFF_FLAG_PARAM);
    const s = q.toString();
    return s ? `?${s}` : '';
  } catch (_) {
    return '';
  }
}

/**
 * The https address the real browser opens: this page's origin and path, the basket, the ad the
 * visit came from. `adVisit` is `currentAdVisit()`'s shape; its platform ad ref rides as
 * `stylon_ad` so the landing is recognised as the same click's (adVisit.isVisitOfAd).
 */
export function handoffUrl({ origin, pathname, items, adVisit = null, token = null, visitorKey = null }) {
  // Written by hand rather than by URLSearchParams, which would spell `:` and `,` as %3A and %2C: the lines stay
  // readable in the address, and `handoffItemsOf` reads either spelling. The query is ours alone — a `variant=` or a
  // tracking tag of the in-app visit has no place in it. With a token the lines stay behind: the server holds them,
  // with everything else the shopper typed, and the address carries only the token.
  const parts = [];
  if (token && HANDOFF_TOKEN_SHAPE.test(String(token))) {
    parts.push(`${HANDOFF_FLAG_PARAM}=${token}`);
  } else {
    const encoded = encodeHandoffItems(items);
    if (encoded) parts.push(`${HANDOFF_ITEMS_PARAM}=${encoded}`);
    parts.push(`${HANDOFF_FLAG_PARAM}=1`);
  }
  if (adVisit && adVisit.campaignId) parts.push(`stylon_cid=${encodeURIComponent(String(adVisit.campaignId))}`);
  if (adVisit && adVisit.platformAdRef) parts.push(`stylon_ad=${encodeURIComponent(String(adVisit.platformAdRef))}`);
  // The in-app visitor key, so the real browser is the same person (utils/visitorKey.adoptHandoffVisitorKey).
  if (visitorKey) parts.push(`stylon_vk=${encodeURIComponent(String(visitorKey))}`);
  return `${String(origin || '').replace(/\/+$/, '')}${pathname || '/'}?${parts.join('&')}`;
}

/**
 * The link that leaves the app: Safari's scheme on iOS; Chrome's intent on Android, falling back
 * to the plain address when Chrome is not there. Elsewhere the address itself. An http address (a
 * local build) is carried the same way; anything that is not a web address is left alone.
 */
export function openInBrowserHref(httpsUrl, os) {
  const url = String(httpsUrl || '');
  const m = /^(https?):\/\/(.+)$/.exec(url);
  if (!m) return url;
  if (os === 'ios') return `x-safari-${url}`;
  if (os === 'android') {
    return `intent://${m[2]}#Intent;scheme=${m[1]};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
  }
  return url;
}
